import React, { memo } from 'react'
import Currency from '@cbgms/components/general/Currency'
import { useTranslation } from 'react-i18next'
import { IProductWithState, ProductType, Surcharge } from '@cbgms/api'
import { Box, Skeleton } from '@mui/material'
import { useShowNetPriceContext } from '@cbgms/components/src/order-parts/context/ShowNetPriceContext'
import { getSurchargeDescription } from '@cbgms/components/src/order-list/hooks/use-surcharge-description'
import { FromPrice } from './FromPrice'
import { LocaleContext } from '@cbgms/base/locale/LocaleContext'
import PriceTableCell from './PriceTableCell'
import { useFeature } from '@cbgms/base/feature-flags'
import OrderButton from '@cbgms/components/src/order-parts/parts/buttons/OrderButton'
import { OrderButtonContextProp } from '@cbgms/components/src/order-parts/context/buttons/OrderButtonContext'
import { GraduatedPricesFromSelector } from '@cbgms/components/src/order-parts/parts/buttons/GraduatedPrices/GraduatedPricesSelector'

interface SharedProps {
  onlySurchargeLabel?: boolean
  onlySurchargeValue?: boolean
  surcharges?: Surcharge[]
}

export interface RegularProps extends SharedProps {
  product: IProductWithState
  hideLabels?: boolean
  onlyLabels?: false
  hideSurcharges?: boolean
  newPriceIndicatorClassName?: string
  workorderUUID?: string
  placeholderLineUUID?: string
  OrderButtonContextProvider?: React.FC<OrderButtonContextProp>
}
interface onlyLabelsProps extends SharedProps {
  product?: never
  hideLabels: false
  onlyLabels: true
  hideSurcharges?: boolean
  newPriceIndicatorClassName?: string
  workorderUUID?: string
  placeholderLineUUID?: string
  OrderButtonContextProvider?: React.FC<OrderButtonContextProp>
} // passing onlyLabels, we expect no product to be passed
type Props = RegularProps | onlyLabelsProps

const shouldRenderPricesForProductType = (product?: IProductWithState): boolean => {
  if (product?.ProductType === ProductType.Pseudo) {
    return false
  }
  return true
}

const PricePlaceholder = () => <Skeleton variant='text' style={{ display: 'inline-block', minWidth: '3em' }} />

const PriceIndicator: React.FC<Props> = memo(
  ({
    product,
    surcharges,
    hideLabels = false,
    onlyLabels = false,
    hideSurcharges = false,
    onlySurchargeLabel = false,
    onlySurchargeValue = false,
    newPriceIndicatorClassName = '',
    OrderButtonContextProvider,
    workorderUUID,
    placeholderLineUUID
  }) => {
    const { formatter } = React.useContext(LocaleContext)
    const { t } = useTranslation()
    const { showNetPrice, showMyPrice } = useShowNetPriceContext()
    const hasMoreProductOnScreenDesign = useFeature('more-products-on-screen-design-v2')
    const mainLabel = showMyPrice ? t('product.MyPrice') : t('product.retail_price')

    if (!shouldRenderPricesForProductType(product)) {
      return null
    }

    return hasMoreProductOnScreenDesign
      ? getPriceIndicatorNewDesign({
          product,
          surcharges,
          showNetPrice,
          showMyPrice,
          hideLabels,
          onlyLabels,
          hideSurcharges,
          onlySurchargeLabel,
          onlySurchargeValue,
          mainLabel,
          formatter,
          newPriceIndicatorClassName,
          t,
          OrderButtonContextProvider,
          workorderUUID,
          placeholderLineUUID
        })
      : getPriceIndicatorOldDesign({
          product,
          surcharges,
          showNetPrice,
          showMyPrice,
          hideLabels,
          onlyLabels,
          hideSurcharges,
          onlySurchargeLabel,
          onlySurchargeValue,
          mainLabel,
          t
        })
  }
)

const getPriceIndicatorOldDesign = (props: any) => {
  const {
    product,
    surcharges,
    showNetPrice,
    showMyPrice,
    hideLabels,
    onlyLabels,
    hideSurcharges,
    onlySurchargeLabel,
    onlySurchargeValue,
    mainLabel,
    t
  } = props

  return (
    <Box sx={{ fontSize: '1rem', lineHeight: '1rem', fontWeight: '500', display: 'flex', justifyContent: 'flex-end' }} textAlign='right'>
      <table>
        <tbody>
          {showNetPrice && (
            <>
              {!hideSurcharges && <FromPrice graduatedPrices={product?.GraduatedPrices} hideLabels={hideLabels} />}
              <tr>
                {!hideLabels && <td>{t('product.netto')}</td>}
                {!onlyLabels && <td>{product?.hasLoadedAvailability ? <Currency amount={product?.NetPrice} /> : <PricePlaceholder />}</td>}
              </tr>
            </>
          )}
          {!onlyLabels &&
            !hideSurcharges &&
            product?.Surcharges?.map((surcharge: Surcharge, key: React.Key | null | undefined) => (
              <tr key={key}>
                {!hideLabels && <td>{getSurchargeDescription(t, surcharge)}</td>}
                {!onlyLabels && (
                  <td>
                    <Currency amount={surcharge.Surcharge} />
                  </td>
                )}
              </tr>
            ))}
          {!hideLabels && onlySurchargeLabel && surcharges && surcharges?.length > 0
            ? surcharges?.map((surcharge: Surcharge, key: React.Key | null | undefined) => (
                <tr key={key}>
                  <td>{getSurchargeDescription(t, surcharge)}</td>
                </tr>
              ))
            : null}
          {!onlyLabels &&
            onlySurchargeValue &&
            (surcharges || []).map((surcharge: any, key: any) => {
              const productSurcharge = product?.Surcharges?.find((productSurcharge: Surcharge) => productSurcharge.Type === surcharge.Type)

              return (
                <tr key={key}>
                  {onlySurchargeValue && <td>{productSurcharge ? <Currency amount={productSurcharge.Surcharge} /> : '-'}</td>}
                </tr>
              )
            })}
          {showNetPrice &&
            showMyPrice && ( // when a shop has a myprice, we also want to show the retail price. we hide it with the net price
              <tr>
                {!hideLabels && <td>{t('product.retail_price')}</td>}
                {!onlyLabels && (
                  <td>
                    <Currency amount={product!.GrossPrice} />
                  </td>
                )}
              </tr>
            )}
          <tr>
            {!hideLabels && <td>{mainLabel}</td>}
            {/* use the calculated price, portal shops don't have a markup configured, and will have rrp as their calculated price */}
            {!onlyLabels && (
              <td>
                <Currency amount={showMyPrice ? product!.CalculatedPrice : product!.GrossPrice} />
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </Box>
  )
}

const getPriceIndicatorNewDesign = (props: any) => {
  const {
    product,
    surcharges,
    showNetPrice,
    showMyPrice,
    hideLabels,
    onlyLabels,
    hideSurcharges,
    onlySurchargeLabel,
    onlySurchargeValue,
    mainLabel,
    formatter,
    newPriceIndicatorClassName,
    t,
    OrderButtonContextProvider,
    workorderUUID,
    placeholderLineUUID
  } = props
  return (
    <Box
      sx={{ fontSize: '1rem', lineHeight: '1rem', fontWeight: '500', display: 'flex', justifyContent: 'flex-end' }}
      className={newPriceIndicatorClassName}
      textAlign='right'
    >
      <table>
        <tbody>
          {showNetPrice && (
            <>
              {!hideSurcharges && OrderButtonContextProvider && (
                <OrderButtonContextProvider
                  productId={product?.ProductID}
                  productIdType={product?.IDType}
                  unitsPerSale={product?.UnitsPerSale}
                  recommendedQuantity={product?.RecommendedQuantity}
                  product={product || undefined}
                  workorderUUID={workorderUUID}
                  placeholderLineUUID={placeholderLineUUID}
                  lineUUID={undefined}
                >
                  <OrderButton disabled={!product?.hasLoadedAvailability} size='medium'>
                    {({ orderLine }: { orderLine: any }) => (
                      <GraduatedPricesFromSelector
                        product={product}
                        hasOrderLine={!!orderLine}
                        currentLineQuantity={orderLine?.Quantity}
                        graduatedPrices={product?.GraduatedPrices}
                        hideLabels={hideLabels}
                        onlyLabels={onlyLabels}
                      />
                    )}
                  </OrderButton>
                </OrderButtonContextProvider>
              )}

              <tr>
                {!hideLabels && <PriceTableCell>{t('product.netto')}:</PriceTableCell>}
                {!onlyLabels &&
                  (product!.hasLoadedAvailability ? (
                    getPriceWithCurrencySymbole(formatter, product!.NetPrice)
                  ) : (
                    <PriceTableCell>
                      <PricePlaceholder />
                    </PriceTableCell>
                  ))}
              </tr>
            </>
          )}
          {!onlyLabels &&
            !hideSurcharges &&
            product?.Surcharges?.map((surcharge: Surcharge) => (
              <tr key={surcharge.Type}>
                {!hideLabels && <PriceTableCell>{getSurchargeDescription(t, surcharge)}:</PriceTableCell>}
                {!onlyLabels && getPriceWithCurrencySymbole(formatter, surcharge.Surcharge)}
              </tr>
            ))}
          {!hideLabels && onlySurchargeLabel && surcharges && surcharges?.length > 0
            ? surcharges?.map((surcharge: Surcharge, key: React.Key | null | undefined) => (
                <tr key={key}>
                  <td>{getSurchargeDescription(t, surcharge)}</td>
                </tr>
              ))
            : null}
          {!onlyLabels &&
            onlySurchargeValue &&
            (surcharges || []).map((surcharge: any, key: any) => {
              const productSurcharge = product?.Surcharges?.find((productSurcharge: Surcharge) => productSurcharge.Type === surcharge.Type)

              return (
                <tr key={key}>
                  {onlySurchargeValue && <td>{productSurcharge ? <Currency amount={productSurcharge.Surcharge} /> : '-'}</td>}
                </tr>
              )
            })}
          {showNetPrice &&
            showMyPrice && ( // when a shop has a myprice, we also want to show the retail price. we hide it with the net price
              <tr>
                {!hideLabels && <PriceTableCell>{t('product.retail_price')}:</PriceTableCell>}
                {(!onlyLabels || onlySurchargeValue) && getPriceWithCurrencySymbole(formatter, product!.GrossPrice)}
              </tr>
            )}
          <tr>
            {!hideLabels && <PriceTableCell>{mainLabel}:</PriceTableCell>}
            {/* use the calculated price, portal shops don't have a markup configured, and will have rrp as their calculated price */}
            {!onlyLabels && getPriceWithCurrencySymbole(formatter, showMyPrice ? product!.CalculatedPrice : product!.GrossPrice)}
          </tr>
        </tbody>
      </table>
    </Box>
  )
}

const getPriceWithCurrencySymbole = (formatter: any, amount: number) => {
  const parts = formatter.formatToParts(amount || 0)
  const currencyPart = parts.find((p: any) => p.type === 'currency')?.value || ''
  const numberPart = parts
    .filter((p: any) => p.type !== 'currency')
    .map((p: any) => p.value)
    .join('')

  return (
    <>
      <PriceTableCell style={{ paddingLeft: '10px' }}>{currencyPart}</PriceTableCell>
      <PriceTableCell style={{ paddingLeft: '10px' }}>{numberPart}</PriceTableCell>
    </>
  )
}

export default PriceIndicator
